import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { useAuth } from '@/app/AuthProvider';
import { supabase } from '@/lib/supabase';
import { blankToNull } from '@/features/admin/model';
import { useProfiles } from '@/features/users/hooks';
import type { SeatProfile } from '@/features/software/model';
import {
  buildOnlineAccountRows,
  type OnlineAccount,
  type OnlineLogin,
  type OnlineShare,
} from './model';
import type {
  Inserts,
  OnlineAccountCategory,
  OnlineAccountShareAccess,
  OnlineAccountStatus,
  OnlineLoginRole,
} from '@/types/database';

export const onlineAccountKeys = {
  all: ['online-accounts'] as const,
  accounts: () => [...onlineAccountKeys.all, 'accounts'] as const,
  logins: () => [...onlineAccountKeys.all, 'logins'] as const,
  shares: () => [...onlineAccountKeys.all, 'shares'] as const,
  any: () => [...onlineAccountKeys.all, 'any'] as const,
};

function requireOrg(organizationId: string | undefined): string {
  if (!organizationId) throw new Error('Not signed in.');
  return organizationId;
}

function useCanQuery(): boolean {
  const { profile, role } = useAuth();
  return !!profile && role !== 'external_lender';
}

export function useOnlineAccounts() {
  const enabled = useCanQuery();
  return useQuery({
    queryKey: onlineAccountKeys.accounts(),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('online_accounts')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OnlineAccount[];
    },
  });
}

/** Every login the caller can see. RLS trims login-scoped shares to their logins. */
export function useOnlineLogins() {
  const enabled = useCanQuery();
  return useQuery({
    queryKey: onlineAccountKeys.logins(),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('online_account_logins')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OnlineLogin[];
    },
  });
}

/** The caller's own shares, plus everyone's on accounts they can share. */
export function useOnlineShares() {
  const enabled = useCanQuery();
  return useQuery({
    queryKey: onlineAccountKeys.shares(),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('online_account_shares')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OnlineShare[];
    },
  });
}

/**
 * Whether to show Accounts in the nav: always for super admins, otherwise
 * only once at least one account has been shared with this person.
 */
export function useHasOnlineAccounts(): boolean {
  const { profile, role } = useAuth();
  const enabled = !!profile && role !== 'external_lender' && role !== 'super_admin';
  const query = useQuery({
    queryKey: onlineAccountKeys.any(),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('online_accounts').select('id').limit(1);
      if (error) return false;
      return (data ?? []).length > 0;
    },
  });
  if (role === 'super_admin') return true;
  return enabled && query.data === true;
}

/**
 * People an account can be shared with: active members other than the
 * caller. Super admins already see everything and lenders never can.
 */
function useShareablePeople(myId: string | null) {
  const people = useProfiles();
  return useMemo(() => {
    const profilesById = new Map<string, SeatProfile>();
    const inactiveIds = new Set<string>();
    for (const p of people.data ?? []) {
      profilesById.set(p.id, p);
      if (!p.is_active || p.role === 'external_lender') inactiveIds.add(p.id);
    }
    const active = (people.data ?? []).filter((p) => !inactiveIds.has(p.id));
    const toSeat = (p: (typeof active)[number]): SeatProfile => ({
      id: p.id,
      full_name: p.full_name,
      email: p.email,
      avatar_url: p.avatar_url,
    });
    return {
      profilesById,
      inactiveIds,
      people: active.map(toSeat),
      shareable: active.filter((p) => p.role !== 'super_admin' && p.id !== myId).map(toSeat),
      superAdmins: active.filter((p) => p.role === 'super_admin').map(toSeat),
      isLoading: people.isLoading,
    };
  }, [people.data, people.isLoading, myId]);
}

export function useOnlineAccountsCatalog() {
  const { profile, role } = useAuth();
  const myId = profile?.id ?? null;
  const accounts = useOnlineAccounts();
  const logins = useOnlineLogins();
  const shares = useOnlineShares();
  const {
    profilesById,
    inactiveIds,
    people,
    shareable,
    superAdmins,
    isLoading: peopleLoading,
  } = useShareablePeople(myId);

  const rows = useMemo(
    () =>
      buildOnlineAccountRows(
        accounts.data ?? [],
        logins.data ?? [],
        shares.data ?? [],
        myId,
        role,
        profilesById,
        inactiveIds,
      ),
    [accounts.data, logins.data, shares.data, myId, role, profilesById, inactiveIds],
  );

  return {
    rows,
    myShares: (shares.data ?? []).filter((s) => s.profile_id === myId),
    profilesById,
    people,
    shareable,
    superAdmins,
    isLoading: accounts.isLoading || logins.isLoading || shares.isLoading || peopleLoading,
    error: accounts.error ?? logins.error ?? shares.error,
  };
}

export interface OnlineAccountInput {
  id?: string;
  name: string;
  category: OnlineAccountCategory;
  status: OnlineAccountStatus;
  website: string;
  subscriptionId: string | null;
  accountNumber: string;
  phone: string;
  address: string;
  openedOn: string;
  openedById: string | null;
  openedByName: string;
  notes: string;
}

/** Resolves to the saved row's id so a create can navigate to its page. */
export function useSaveOnlineAccount() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: OnlineAccountInput): Promise<string> => {
      const organizationId = requireOrg(profile?.organization_id);
      const name = input.name.trim();
      if (!name) throw new Error('Give the account a name.');
      const fields = {
        name,
        category: input.category,
        status: input.status,
        website: blankToNull(input.website),
        subscription_id: input.subscriptionId,
        account_number: blankToNull(input.accountNumber),
        phone: blankToNull(input.phone),
        address: blankToNull(input.address),
        opened_on: blankToNull(input.openedOn),
        opened_by: input.openedById,
        opened_by_name: input.openedById ? null : blankToNull(input.openedByName),
        notes: blankToNull(input.notes),
      };

      if (input.id) {
        const { data, error } = await supabase
          .from('online_accounts')
          .update(fields)
          .eq('id', input.id)
          .select('id');
        if (error) throw error;
        if (!data?.length) throw new Error('You don’t have permission to change this account.');
        return input.id;
      }

      const payload: Inserts<'online_accounts'> = {
        organization_id: organizationId,
        created_by: profile?.id ?? null,
        ...fields,
      };
      const { data, error } = await supabase
        .from('online_accounts')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.accounts() });
    },
  });
}

export function useDeleteOnlineAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('online_accounts')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('Only a super admin can delete an account.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.all });
    },
  });
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Something went wrong.';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const body = (await ctx.clone().json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

type SecretsRequest =
  | { action: 'reveal'; login_id: string; purpose: 'view' | 'copy' }
  | { action: 'set'; login_id: string; password: string }
  | { action: 'clear'; login_id: string };

async function callSecrets<T>(body: SecretsRequest): Promise<T> {
  const { data, error } = await supabase.functions.invoke('online-account-secrets', { body });
  if (error) throw new Error(await extractFunctionErrorMessage(error));
  const result = data as { ok?: boolean; error?: string } & T;
  if (result?.ok === false) throw new Error(result.error ?? 'Something went wrong.');
  return result;
}

/** Fetches a saved password. Each call is written to the audit log. */
export async function revealLoginPassword(
  loginId: string,
  purpose: 'view' | 'copy',
): Promise<string> {
  const { password } = await callSecrets<{ password: string }>({
    action: 'reveal',
    login_id: loginId,
    purpose,
  });
  return password;
}

export type PasswordChange = { kind: 'keep' } | { kind: 'set'; password: string } | { kind: 'clear' };

async function applyPasswordChange(loginId: string, change: PasswordChange) {
  if (change.kind === 'set') {
    await callSecrets({ action: 'set', login_id: loginId, password: change.password });
  } else if (change.kind === 'clear') {
    await callSecrets({ action: 'clear', login_id: loginId });
  }
}

export interface OnlineLoginInput {
  id?: string;
  accountId: string;
  role: OnlineLoginRole;
  roleDetail: string;
  profileId: string | null;
  holderName: string;
  username: string;
  email: string;
  twoFactor: string;
  notes: string;
  password: PasswordChange;
}

export function useSaveOnlineLogin() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: OnlineLoginInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const username = blankToNull(input.username);
      const email = blankToNull(input.email);
      if (!username && !email) throw new Error('Enter a username or an email for the login.');
      const fields = {
        role: input.role,
        role_detail: blankToNull(input.roleDetail),
        profile_id: input.profileId,
        holder_name: input.profileId ? null : blankToNull(input.holderName),
        username,
        email,
        two_factor: blankToNull(input.twoFactor),
        notes: blankToNull(input.notes),
      };

      let loginId = input.id;
      if (loginId) {
        const { data, error } = await supabase
          .from('online_account_logins')
          .update(fields)
          .eq('id', loginId)
          .select('id');
        if (error) throw error;
        if (!data?.length) throw new Error('You don’t have permission to change this login.');
      } else {
        const payload: Inserts<'online_account_logins'> = {
          organization_id: organizationId,
          account_id: input.accountId,
          created_by: profile?.id ?? null,
          ...fields,
        };
        const { data, error } = await supabase
          .from('online_account_logins')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        loginId = (data as { id: string }).id;
      }

      try {
        await applyPasswordChange(loginId, input.password);
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'Something went wrong.';
        throw new Error(`The login was saved, but the password wasn’t: ${reason}`);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.logins() });
    },
  });
}

/** Password-only change, for people with edit access to a single login. */
export function useChangeLoginPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loginId, change }: { loginId: string; change: PasswordChange }) => {
      await applyPasswordChange(loginId, change);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.logins() });
    },
  });
}

export function useDeleteOnlineLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('online_account_logins')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You don’t have permission to remove this login.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.all });
    },
  });
}

export interface AccessInput {
  accountId: string;
  profileId: string;
  access: OnlineAccountShareAccess;
  /** Empty for the whole account. */
  loginIds: string[];
  /** `YYYY-MM-DD`; access ends at the end of that day. */
  expiresOn: string;
}

function endOfDayIso(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

export function useSetOnlineAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AccessInput | AccessInput[]) => {
      const list = Array.isArray(input) ? input : [input];
      for (const item of list) {
        const { error } = await supabase.rpc('set_online_account_access', {
          p_account_id: item.accountId,
          p_profile_id: item.profileId,
          p_access: item.access,
          p_login_ids: item.loginIds.length ? item.loginIds : null,
          p_expires_at: item.expiresOn ? endOfDayIso(item.expiresOn) : null,
        });
        if (error) throw error;
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.shares() });
    },
  });
}

export function useRevokeOnlineAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, profileId }: { accountId: string; profileId: string }) => {
      const { error } = await supabase.rpc('revoke_online_account_access', {
        p_account_id: accountId,
        p_profile_id: profileId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineAccountKeys.shares() });
    },
  });
}
